@if (
  {
    messages: (messages$ | async) ?? [],
    pending: (pending$ | async) ?? false,
    voiceEnabled: (voiceEnabled$ | async) ?? false,
    speech: speech$ | async
  };
  as vm
) {
  <div #log class="chat-log flex-grow-1 overflow-auto d-flex flex-column gap-2 p-3" (click)="followLink($event)">
    @for (message of vm.messages; track message.id) {
      @if (message.role === 'user') {
        <div class="chat-row user">
          <span class="chat-avatar user" aria-hidden="true"><i class="bi bi-person-fill"></i></span>
          @if (message.transcribing) {
            <div class="chat-message user transcribing" role="status">
              <i class="bi bi-mic-fill me-1" aria-hidden="true"></i>Transcribing…
            </div>
          } @else {
            <div class="chat-message user">
              @if (message.languageCode) {
                <i class="bi bi-mic-fill me-1 opacity-75" aria-hidden="true" title="Asked aloud"></i>
              }
              {{ message.text }}
            </div>
          }
        </div>
      } @else {
        <div class="chat-row assistant">
          <span class="chat-avatar assistant" aria-hidden="true"><i class="bi bi-robot"></i></span>
          @if (message.error) {
            <div class="chat-message assistant error">
              <i class="bi bi-exclamation-triangle me-1"></i>{{ message.text }}
            </div>
          } @else {
            <div class="chat-answer d-flex flex-column align-items-start gap-1">
              <!-- Angular sanitizes [innerHTML] on the way in. Never replace this with bypassSecurityTrustHtml. -->
              <div class="chat-message assistant chat-markdown" [innerHTML]="message.html"></div>
              @if (vm.voiceEnabled) {
                <button
                  type="button"
                  class="btn btn-sm btn-link chat-listen p-0 d-inline-flex align-items-center gap-1"
                  [class.active]="playbackOf(message.id, vm.speech)"
                  [disabled]="playbackOf(message.id, vm.speech) === 'loading'"
                  (click)="toggleSpeech(message.id)"
                  [attr.aria-pressed]="playbackOf(message.id, vm.speech) === 'playing'"
                >
                  @switch (playbackOf(message.id, vm.speech)) {
                    @case ('loading') {
                      <span class="spinner-border spinner-border-sm" aria-hidden="true"></span>
                      <span>Loading audio…</span>
                    }
                    @case ('playing') {
                      <i class="bi bi-stop-fill" aria-hidden="true"></i>
                      <span>Stop</span>
                    }
                    @default {
                      <i class="bi bi-volume-up-fill" aria-hidden="true"></i>
                      <span>Listen</span>
                    }
                  }
                </button>
              }
            </div>
          }
        </div>
      }
    } @empty {
      @if (!vm.pending) {
        <div class="chat-empty my-auto text-center">
          <i class="bi bi-stars" aria-hidden="true"></i>
          <p class="mb-0">Ask anything about PreCalc: a screen, a field, or how a number is calculated.</p>
        </div>
      }
    }

    @if (vm.pending) {
      <div class="chat-row assistant">
        <span class="chat-avatar assistant" aria-hidden="true"><i class="bi bi-robot"></i></span>
        <div class="chat-message assistant typing" role="status">
          <span class="typing-dots" aria-hidden="true"><span></span><span></span><span></span></span>
          <span class="visually-hidden">Thinking…</span>
        </div>
      </div>
    }
  </div>

  <div class="chat-composer border-top p-2 d-flex flex-column gap-2">
    <form class="d-flex gap-2" (submit)="send(); $event.preventDefault()">
      @if (vm.messages.length) {
        <button
          type="button"
          class="btn btn-sm btn-link text-body-secondary px-1 d-flex align-items-center"
          [disabled]="vm.pending || recording"
          (click)="clear()"
          aria-label="Clear the conversation"
          title="Clear the conversation"
        >
          <i class="bi bi-trash"></i>
        </button>
      }
      @if (recording) {
        <!-- Stands in for the text box while recording: the level shows the microphone is really
           picking something up, which is the one thing a person cannot tell otherwise. -->
        <div
          class="chat-level form-control form-control-sm d-flex align-items-center gap-2"
          role="status"
          [class.muted]="muted"
        >
          @if (muted) {
            <i class="bi bi-mic-mute-fill" aria-hidden="true"></i>
            <span class="text-truncate">The microphone delivers no sound</span>
          } @else {
            <i class="bi bi-soundwave" aria-hidden="true"></i>
            <span class="chat-level-track flex-grow-1" aria-hidden="true">
              <span class="chat-level-fill" [style.width.%]="level * 100"></span>
            </span>
            <span class="visually-hidden">Recording</span>
          }
        </div>
      } @else {
        <input
          type="text"
          id="help-chat-question"
          class="form-control form-control-sm"
          maxlength="2000"
          placeholder="Ask about PreCalc…"
          aria-label="Your question"
          [value]="draft"
          [disabled]="vm.pending"
          (input)="updateDraft($event)"
        />
      }
      @if (vm.voiceEnabled && canRecord) {
        <button
          type="button"
          class="btn btn-sm chat-mic d-flex align-items-center"
          [class.btn-outline-secondary]="!recording"
          [class.btn-danger]="recording"
          [class.recording]="recording"
          [disabled]="vm.pending"
          (click)="toggleRecording()"
          [attr.aria-pressed]="recording"
          [attr.aria-label]="recording ? 'Stop and send' : 'Ask aloud'"
          [title]="recording ? 'Stop and send' : 'Ask aloud'"
        >
          <i class="bi" [class.bi-mic-fill]="!recording" [class.bi-stop-fill]="recording"></i>
        </button>
      }
      <button
        type="submit"
        class="btn btn-sm btn-primary d-flex align-items-center"
        [disabled]="!draft.trim() || vm.pending || recording"
        aria-label="Send"
        title="Send"
      >
        <i class="bi bi-send"></i>
      </button>
    </form>

    <div class="d-flex align-items-center gap-3">
      <div class="form-check form-switch chat-voice-switch m-0 d-flex align-items-center gap-2">
        <input
          class="form-check-input m-0"
          type="checkbox"
          role="switch"
          id="help-chat-voice"
          [checked]="vm.voiceEnabled"
          (change)="toggleVoice($event)"
        />
        <label
          class="form-check-label small"
          for="help-chat-voice"
          title="Ask aloud with the microphone, and listen to the answers"
        >
          Voice
        </label>
      </div>

      <!-- Offered only when there is a choice: with one microphone the browser's default is the
         right one, and a list of one is just noise. -->
      @if (vm.voiceEnabled && canRecord && microphones.length > 1) {
        <select
          id="help-chat-microphone"
          class="form-select form-select-sm chat-microphone flex-grow-1 small"
          [disabled]="recording"
          (change)="selectMicrophone($event)"
          aria-label="Microphone"
          title="Microphone"
        >
          <option value="" [selected]="!microphoneId">Default microphone</option>
          @for (microphone of microphones; track microphone.deviceId) {
            <option [value]="microphone.deviceId" [selected]="microphone.deviceId === microphoneId">
              {{ microphone.label }}
            </option>
          }
        </select>
      }
    </div>
  </div>
}
